import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage, API } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { TourLauncherButton } from "@/contexts/TourContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, FileDown, RotateCcw, DollarSign, AlertTriangle, CheckCircle2, Home, User, Mail, Inbox, X } from "lucide-react";
import { toast } from "sonner";

/**
 * R152 — Estoppel Certificate generator.
 *
 * Admins can request a fresh certificate for any unit (title company
 * closing gates on this). Two-step UI:
 *   1. Fill unit + closing details → click Preview to see the live
 *      balance from db.charges + open violations + governing rules.
 *   2. Confirm → certificate is committed to db.estoppel_certificates
 *      and the branded PDF becomes downloadable.
 *
 * Prior certificates are listed below for re-download or rescission.
 */
export default function EstoppelPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [certs, setCerts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailingCert, setEmailingCert] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [form, setForm] = useState({
    unit: "",
    buyer_name: "",
    title_company: "",
    closing_date: "",
    special_assessment_pending_usd: 0,
    transfer_fee_usd: 100,
    capital_contribution_usd: 0,
    notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const load = useCallback(async () => {
    if (!scope) return;
    try {
      const [certResp, reqResp] = await Promise.all([
        api.get("/admin/estoppel", { params: { property_id: scope } }),
        api.get("/admin/estoppel-requests", { params: { property_id: scope, status: "pending" } }),
      ]);
      setCerts(certResp.data.items || []);
      setRequests(reqResp.data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load certificates"); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const buildPayload = () => ({
    property_id: scope,
    unit: form.unit.trim(),
    buyer_name: form.buyer_name || undefined,
    title_company: form.title_company || undefined,
    closing_date: form.closing_date || undefined,
    special_assessment_pending_usd: Number(form.special_assessment_pending_usd) || 0,
    transfer_fee_usd: Number(form.transfer_fee_usd) || 0,
    capital_contribution_usd: Number(form.capital_contribution_usd) || 0,
    notes: form.notes || undefined,
  });

  const runPreview = async () => {
    if (!form.unit.trim()) return toast.error("Unit number is required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/estoppel/preview", buildPayload());
      setPreview(data.preview);
    } catch (e) { toast.error(apiErrorMessage(e) || "Preview failed"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    if (!form.unit.trim()) return toast.error("Unit number is required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/estoppel/generate", buildPayload());
      toast.success(`Certificate ${data.certificate.cert_number} generated`);
      setDialogOpen(false);
      setPreview(null);
      setForm({ ...form, unit: "", buyer_name: "", title_company: "", closing_date: "", notes: "" });
      await load();
      // Open PDF in a new tab immediately for convenience.
      window.open(`${API}/admin/estoppel/${data.certificate.id}.pdf`, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiErrorMessage(e) || "Generate failed"); }
    finally { setBusy(false); }
  };

  const rescind = async (c) => {
    if (!window.confirm(`Rescind ${c.cert_number}? Prior downloads remain valid but a fresh certificate should be issued.`)) return;
    try {
      await api.post(`/admin/estoppel/${c.id}/rescind`);
      toast.success("Rescinded");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Rescind failed"); }
  };

  const startEmail = (c) => {
    setEmailingCert(c);
    setEmailTo(c.buyer_name ? "" : "");
    setEmailMsg("");
  };

  const sendEmail = async () => {
    if (!emailingCert) return;
    if (!/^\S+@\S+\.\S+$/.test(emailTo)) return toast.error("Enter a valid email");
    try {
      const { data } = await api.post(`/admin/estoppel/${emailingCert.id}/email`,
        { to_email: emailTo, message: emailMsg });
      if (data.ok) toast.success(`Emailed via ${data.delivery?.provider || "provider"}`);
      else toast.warning(`Queued — will retry (${data.delivery?.error || ""})`);
      setEmailingCert(null); setEmailTo(""); setEmailMsg("");
    } catch (e) { toast.error(apiErrorMessage(e) || "Email failed"); }
  };

  const fulfillRequest = async (r) => {
    if (!window.confirm(`Generate estoppel for Unit ${r.unit} and email to ${r.to_email}?`)) return;
    try {
      const { data } = await api.post(`/admin/estoppel-requests/${r.id}/generate`,
        { transfer_fee_usd: 100, capital_contribution_usd: 0 });
      if (data.email_sent) toast.success(`Fulfilled — ${data.cert_number} emailed`);
      else toast.warning(`${data.cert_number} generated (email queued)`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Fulfill failed"); }
  };

  const declineRequest = async (r) => {
    if (!window.confirm(`Decline this title-co request?`)) return;
    try {
      await api.post(`/admin/estoppel-requests/${r.id}/decline`);
      toast.success("Declined");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Decline failed"); }
  };

  const scopeName = useMemo(
    () => properties.find((p) => p.id === scope)?.name || scope,
    [properties, scope],
  );

  return (
    <div className="space-y-4" data-testid="estoppel-page">
      <PageHeader
        eyebrow="Finance"
        title="Estoppel Certificates"
        subtitle="Generate resale certificates title companies request during a unit sale. Balances lock at issue time."
        actions={<TourLauncherButton name="estoppel_tour" />}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-64 h-9 bg-white/[0.04] border-white/10"
                          data-testid="estoppel-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {requests.length > 0 && (
          <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10 text-xs h-9 px-3"
                 data-testid="estoppel-pending-badge">
            <Inbox className="h-3 w-3 mr-1" /> {requests.length} pending request{requests.length === 1 ? "" : "s"}
          </Badge>
        )}
        <div className="ml-auto">
          <Button onClick={() => { setDialogOpen(true); setPreview(null); }}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                  data-testid="estoppel-new-btn" data-tour="estoppel-new">
            <Plus className="h-4 w-4 mr-1" /> New certificate
          </Button>
        </div>
      </div>

      {requests.length > 0 && (
        <Card className="p-0 border-amber-500/30 overflow-hidden bg-amber-500/[0.03]"
              data-testid="estoppel-requests-inbox">
          <div className="p-4 border-b border-amber-500/20 flex items-center gap-2">
            <Inbox className="h-4 w-4 text-amber-400" />
            <div className="overline">Title-company requests — {scopeName}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-amber-500/[0.05] text-xs">
              <tr>
                <th className="text-left px-4 py-2 text-white/60">Received</th>
                <th className="text-left px-4 py-2 text-white/60">Unit</th>
                <th className="text-left px-4 py-2 text-white/60">Title company</th>
                <th className="text-left px-4 py-2 text-white/60">Deliver to</th>
                <th className="text-left px-4 py-2 text-white/60">Closing</th>
                <th className="text-right px-4 py-2 text-white/60"></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-t border-amber-500/10"
                    data-testid={`estoppel-request-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs text-white/50">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2 font-medium">{r.unit}</td>
                  <td className="px-4 py-2 text-xs">{r.title_company}</td>
                  <td className="px-4 py-2 text-xs font-mono">{r.to_email}</td>
                  <td className="px-4 py-2 text-xs text-white/70">
                    {r.closing_date ? new Date(r.closing_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => fulfillRequest(r)}
                            className="text-xs px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`estoppel-fulfill-${r.id}`}>
                      <CheckCircle2 className="h-3 w-3 inline mr-1" /> Generate & Email
                    </button>
                    <button onClick={() => declineRequest(r)}
                            className="ml-2 text-xs px-2 py-1 rounded-md border border-white/10 text-white/50 hover:text-rose-300 hover:border-rose-500/40"
                            data-testid={`estoppel-decline-${r.id}`}>
                      <X className="h-3 w-3 inline mr-1" /> Decline
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="p-0 border-white/10 overflow-hidden" data-tour="estoppel-history">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-orange-400" />
            <div className="overline">History — {scopeName}</div>
            <Badge variant="outline" className="text-[10px] border-white/10">
              {certs.length}
            </Badge>
          </div>
        </div>
        {certs.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No certificates yet. Click <strong>New certificate</strong> to generate one for a title company.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs">
              <tr>
                <th className="text-left px-4 py-2 text-white/60">Cert #</th>
                <th className="text-left px-4 py-2 text-white/60">Unit</th>
                <th className="text-left px-4 py-2 text-white/60">Owner</th>
                <th className="text-left px-4 py-2 text-white/60">Buyer</th>
                <th className="text-right px-4 py-2 text-white/60">Total due</th>
                <th className="text-left px-4 py-2 text-white/60">Issued</th>
                <th className="text-left px-4 py-2 text-white/60">Status</th>
                <th className="text-right px-4 py-2 text-white/60"></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id} className="border-t border-white/5"
                    data-testid={`estoppel-row-${c.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{c.cert_number}</td>
                  <td className="px-4 py-2 font-medium">{c.unit}</td>
                  <td className="px-4 py-2 text-xs text-white/70">{c.owner?.name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-white/70">{c.buyer_name || <span className="text-white/30">—</span>}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${c.total_due_usd > 0 ? "text-rose-300" : "text-emerald-300"}`}>
                    ${(c.total_due_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-2 text-xs text-white/60">
                    {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {c.status === "rescinded"
                      ? <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">Rescinded</Badge>
                      : <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">Active</Badge>}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <a href={`${API}/admin/estoppel/${c.id}.pdf`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                       data-testid={`estoppel-pdf-${c.id}`}>
                      <FileDown className="h-3 w-3" /> PDF
                    </a>
                    {c.status !== "rescinded" && (
                      <button onClick={() => startEmail(c)}
                              className="ml-2 text-xs px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                              data-testid={`estoppel-email-${c.id}`}>
                        <Mail className="h-3 w-3 inline mr-1" /> Email
                      </button>
                    )}
                    {c.status !== "rescinded" && (
                      <button onClick={() => rescind(c)}
                              className="ml-2 text-xs px-2 py-1 rounded-md border border-white/10 text-white/60 hover:text-rose-300 hover:border-rose-500/40"
                              data-testid={`estoppel-rescind-${c.id}`}>
                        <RotateCcw className="h-3 w-3 inline mr-1" /> Rescind
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[720px] max-h-[90vh] overflow-y-auto"
                       data-testid="estoppel-new-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-orange-400" />
              New estoppel certificate
            </DialogTitle>
            <DialogDescription className="text-xs text-white/60">
              Preview locks the live balance from ledger + open violations, then commits a title-company-ready PDF snapshot.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unit number</Label>
                <Input value={form.unit}
                        onChange={(e) => setForm({ ...form, unit: e.target.value })}
                        placeholder="101"
                        className="bg-white/[0.04] border-white/10"
                        data-testid="estoppel-unit-input" />
              </div>
              <div>
                <Label className="text-xs">Scheduled closing date</Label>
                <Input type="date" value={form.closing_date}
                        onChange={(e) => setForm({ ...form, closing_date: e.target.value })}
                        className="bg-white/[0.04] border-white/10"
                        data-testid="estoppel-closing-date" />
              </div>
              <div>
                <Label className="text-xs">Buyer name</Label>
                <Input value={form.buyer_name}
                        onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                        placeholder="Jane & John Smith"
                        className="bg-white/[0.04] border-white/10"
                        data-testid="estoppel-buyer" />
              </div>
              <div>
                <Label className="text-xs">Title company</Label>
                <Input value={form.title_company}
                        onChange={(e) => setForm({ ...form, title_company: e.target.value })}
                        placeholder="Sunshine Title Co."
                        className="bg-white/[0.04] border-white/10"
                        data-testid="estoppel-title-co" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Pending assessment ($)</Label>
                <Input type="number" min={0} step={0.01}
                        value={form.special_assessment_pending_usd}
                        onChange={(e) => setForm({ ...form, special_assessment_pending_usd: e.target.value })}
                        className="bg-white/[0.04] border-white/10 tabular-nums"
                        data-testid="estoppel-special-assessment" />
              </div>
              <div>
                <Label className="text-xs">Transfer fee ($)</Label>
                <Input type="number" min={0} step={0.01}
                        value={form.transfer_fee_usd}
                        onChange={(e) => setForm({ ...form, transfer_fee_usd: e.target.value })}
                        className="bg-white/[0.04] border-white/10 tabular-nums"
                        data-testid="estoppel-transfer-fee" />
              </div>
              <div>
                <Label className="text-xs">Capital contribution ($)</Label>
                <Input type="number" min={0} step={0.01}
                        value={form.capital_contribution_usd}
                        onChange={(e) => setForm({ ...form, capital_contribution_usd: e.target.value })}
                        className="bg-white/[0.04] border-white/10 tabular-nums"
                        data-testid="estoppel-capital-contrib" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={2}
                        className="bg-white/[0.04] border-white/10 text-sm"
                        data-testid="estoppel-notes" />
            </div>

            {preview && (
              <div className="rounded-md border border-white/10 p-3 bg-white/[0.02]"
                   data-testid="estoppel-preview">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {preview.total_due_usd > 0
                      ? <AlertTriangle className="h-4 w-4 text-rose-400" />
                      : <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                    <div className="overline">Live balance preview</div>
                  </div>
                  <div className={`text-2xl font-semibold tabular-nums ${preview.total_due_usd > 0 ? "text-rose-300" : "text-emerald-300"}`}>
                    ${preview.total_due_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-white/70">
                  <div className="flex items-center gap-2">
                    <User className="h-3 w-3 text-white/40" />
                    <span>Owner: <strong>{preview.owner?.name || "—"}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Home className="h-3 w-3 text-white/40" />
                    <span>Unit: <strong>{preview.unit}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-3 w-3 text-white/40" />
                    <span>Monthly dues: <strong>${preview.monthly_dues_usd?.toFixed?.(2) || "0.00"}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-3 w-3 text-white/40" />
                    <span>Open charges: <strong>{preview.charges?.length || 0}</strong> · Violations: <strong>{preview.violations?.length || 0}</strong></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}
                    className="border-white/10">Cancel</Button>
            <Button variant="outline" onClick={runPreview} disabled={busy || !form.unit.trim()}
                    className="border-orange-500/40 text-orange-300"
                    data-testid="estoppel-preview-btn">
              Preview balance
            </Button>
            <Button onClick={generate} disabled={busy || !form.unit.trim()}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="estoppel-generate-btn">
              <FileDown className="h-4 w-4 mr-1" /> Generate certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email delivery dialog */}
      <Dialog open={!!emailingCert} onOpenChange={(o) => !o && setEmailingCert(null)}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]"
                       data-testid="estoppel-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-emerald-400" /> Email certificate
            </DialogTitle>
            <DialogDescription className="text-xs text-white/60">
              PDF will be attached fresh — recipient always gets the latest balance snapshot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recipient email</Label>
              <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                     placeholder="closings@titleco.com"
                     className="bg-white/[0.04] border-white/10 mt-1"
                     data-testid="estoppel-email-to" />
            </div>
            <div>
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={emailMsg} rows={3}
                        onChange={(e) => setEmailMsg(e.target.value)}
                        placeholder="Any special instructions for the title company…"
                        className="bg-white/[0.04] border-white/10 mt-1 text-sm"
                        data-testid="estoppel-email-msg" />
            </div>
            {emailingCert && (
              <div className="text-xs text-white/50 rounded-md border border-white/10 bg-white/[0.02] p-2">
                Attaching <strong>{emailingCert.cert_number}</strong> — Unit {emailingCert.unit} — ${(emailingCert.total_due_usd || 0).toFixed(2)} due
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailingCert(null)}
                    className="border-white/10">Cancel</Button>
            <Button onClick={sendEmail}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="estoppel-email-send-btn">
              <Mail className="h-4 w-4 mr-1" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
